import { Close as CloseIcon } from "@mui/icons-material";
import { Box, Button, Chip, IconButton, Theme, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import React from "react";

import { monospaceStyle, panelShadow } from "../../../theme";
import {
  PartnershipCluster,
  PartnershipNode,
  PartnershipPair,
  PartnershipProductWork,
  PartnershipProjectWork,
  Partnerships,
  PartnershipWorkGroup,
} from "../../../types/analytics";
import { EntityClickHandler, getEntityTypeEntry } from "../../../types/entityRegistry";
import { EntityRef } from "../../../types/productPlane";
import { projectTypeMapping } from "../../../utils/projectTypeMapping";
import { CountryFlag, countryName } from "../../common/CountryFlag";
import { OrganizationChip } from "../../common/OrganizationChip";
import { formatCode } from "../../Explore/productFields";
import { counted } from "../Ecosystem/EcosystemParts";
import { rankedPartners } from "./partnershipsLayout";
import {
  clusterColour,
  groupName,
  strengthText,
  tieMakeup,
} from "./PartnershipsParts";

// What the view has selected: a cluster, a group, or the tie of a pair
export type PartnershipsSelection =
  | { kind: "cluster"; id: number }
  | { kind: "group"; id: string }
  | { kind: "pair"; a: string; b: string }
  | null;

// How a tie's strength is counted, in words
export const STRENGTH_WORDS =
  "Each shared project adds 1 ÷ (team size − 1): two on a project of their own count 1, two in a team of five count ¼. A product made together adds 1, a stated partnership 1.";

// Ties listed in a section before "+n more"
const LISTED = 6;

// A small uppercase head, as the fields of the designs carry
const HEAD_SX = {
  fontSize: 11,
  fontWeight: 500,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: "text.secondary",
} as const;

// An action that reads as a link
const ACTION_SX = {
  p: 0,
  minWidth: 0,
  textTransform: "none",
  fontSize: "12.5px",
  fontWeight: 400,
  lineHeight: 1.5,
  justifyContent: "flex-start",
  textAlign: "left",
  "&:hover": { backgroundColor: "transparent", textDecoration: "underline" },
} as const;

// A monospace caption, as the panel's counts are written
const CAPTION_SX = { ...monospaceStyle, fontSize: "11px", lineHeight: 1.4 } as const;

// The panel's surface: raised over the top right of the canvas, as the
// ecosystem's, or docked beside it
const PanelShell: React.FC<{
  label: string;
  docked?: boolean;
  children: React.ReactNode;
}> = ({ label, docked = false, children }) => (
  <Box
    component="aside"
    aria-label={label}
    sx={{
      ...(docked
        ? {
            position: "relative",
            flex: "0 0 400px",
            borderLeft: 1,
            borderColor: "divider",
            backgroundColor: "background.raised",
          }
        : {
            position: "absolute",
            top: 14,
            right: 14,
            zIndex: 5,
            width: 400,
            maxWidth: "calc(100% - 28px)",
            maxHeight: "calc(100% - 28px)",
            border: 1,
            borderColor: "divider",
            borderRadius: (theme: Theme) => `${theme.radius.surface}px`,
            backgroundColor: "background.raised",
            boxShadow: (theme: Theme) => panelShadow(theme),
          }),
      boxSizing: "border-box",
      display: "flex",
      flexDirection: "column",
      gap: 0.75,
      p: "14px",
      overflow: "auto",
      fontSize: "12.5px",
    }}
  >
    {children}
  </Box>
);

// The name of an entity: it opens the entity's detail where the detail
// views know its type, else it is plain text
const EntityName: React.FC<{
  entity: EntityRef;
  onOpenEntity: EntityClickHandler;
  sx?: object;
}> = ({ entity, onOpenEntity, sx }) => {
  const name = entity.name ?? entity.id;
  return getEntityTypeEntry(entity.type) ? (
    <Button
      size="small"
      title={`Open the ${entity.type}`}
      onClick={() => onOpenEntity(entity.id, entity.type)}
      sx={{ ...ACTION_SX, ...sx }}
    >
      {name}
    </Button>
  ) : (
    <Box component="span" sx={{ lineHeight: 1.5 }}>
      {name}
    </Box>
  );
};

// The heading of a panel: a small head, then the title, and the button
// that clears the selection
const PanelHead: React.FC<{
  head: string;
  onClose?: () => void;
  children: React.ReactNode;
}> = ({ head, onClose, children }) => (
  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Box sx={HEAD_SX}>{head}</Box>
      <Typography
        component="h3"
        sx={{
          mt: 0.25,
          fontSize: 14,
          fontWeight: 600,
          lineHeight: 1.45,
          overflowWrap: "anywhere",
        }}
      >
        {children}
      </Typography>
    </Box>
    {onClose && (
      <IconButton size="small" aria-label="Clear selection" onClick={onClose} sx={{ m: "-4px" }}>
        <CloseIcon fontSize="small" />
      </IconButton>
    )}
  </Box>
);

// A title's name that opens the entity
const TITLE_NAME_SX = {
  fontSize: "inherit",
  fontWeight: "inherit",
  lineHeight: "inherit",
  color: "text.primary",
  display: "inline",
  verticalAlign: "baseline",
} as const;

// A section: a small head, then its entries in a list, then how many more
const Section: React.FC<{
  label: string;
  more?: number;
  children: React.ReactNode;
}> = ({ label, more = 0, children }) => (
  <Box component="section" aria-label={label} sx={{ mt: 0.75 }}>
    <Box sx={HEAD_SX}>{label}</Box>
    <Box
      component="ul"
      sx={{ m: 0, mt: 0.5, p: 0, listStyle: "none", display: "grid", gap: 0.5 }}
    >
      {children}
      {more > 0 && (
        <Box component="li" sx={{ ...CAPTION_SX, color: "text.secondary" }}>
          +{more} more
        </Box>
      )}
    </Box>
  </Box>
);

// A small dot in a cluster's colour
const Dot: React.FC<{ colour: string }> = ({ colour }) => (
  <Box
    component="span"
    aria-hidden
    sx={{
      display: "inline-block",
      flexShrink: 0,
      width: 9,
      height: 9,
      borderRadius: "50%",
      border: `1.5px solid ${colour}`,
      backgroundColor: alpha(colour, 0.35),
    }}
  />
);

// A share of a group's work, as a whole percentage
const shareText = (share: number): string => `${Math.round(share * 100)}%`;

// The shared projects of a pair, each opening the project, and how many
// more there are
const SharedProjects: React.FC<{
  pair: PartnershipPair;
  onOpenEntity: EntityClickHandler;
}> = ({ pair, onOpenEntity }) => (
  <Box
    component="span"
    sx={{ display: "flex", flexWrap: "wrap", columnGap: 1, rowGap: 0, alignItems: "baseline" }}
  >
    {pair.shared.map((project) => (
      <EntityName
        key={project.id}
        entity={project}
        onOpenEntity={onOpenEntity}
        sx={{ fontSize: "11.5px", color: "link.main" }}
      />
    ))}
    {pair.projects > pair.shared.length && (
      <Box component="span" sx={{ ...CAPTION_SX, color: "text.secondary" }}>
        +{pair.projects - pair.shared.length} more
      </Box>
    )}
  </Box>
);

// A tie listed by its two names and its strength: the row selects the pair
const PairRow: React.FC<{
  pair: PartnershipPair;
  byId: Map<string, PartnershipNode>;
  onSelect: (selection: PartnershipsSelection) => void;
}> = ({ pair, byId, onSelect }) => {
  const name = (id: string) => {
    const node = byId.get(id);
    return node ? groupName(node) : id;
  };
  return (
    <Box component="li" sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
      <Button
        size="small"
        onClick={() => onSelect({ kind: "pair", a: pair.a, b: pair.b })}
        sx={{ ...ACTION_SX, flex: 1, minWidth: 0 }}
      >
        {name(pair.a)} × {name(pair.b)}
      </Button>
      <Typography component="span" color="text.secondary" sx={CAPTION_SX}>
        {strengthText(pair.strength)}
      </Typography>
    </Box>
  );
};

// How a tie's strength is counted, beneath a rule
const StrengthNote: React.FC = () => (
  <Box sx={{ mt: 1, pt: 1, borderTop: 1, borderColor: "divider" }}>
    <Box sx={HEAD_SX}>Strength</Box>
    <Typography variant="caption" component="div" color="text.secondary" sx={{ mt: 0.25, lineHeight: 1.45 }}>
      {STRENGTH_WORDS}
    </Typography>
  </Box>
);

// A product's or a project's status, as the export words it, in a small tag
const StatusTag: React.FC<{ status: string | null }> = ({ status }) =>
  status ? (
    <Box
      component="span"
      sx={{
        ...CAPTION_SX,
        flexShrink: 0,
        px: 0.75,
        borderRadius: 999,
        border: 1,
        borderColor: "divider",
        color: "text.secondary",
        whiteSpace: "nowrap",
      }}
    >
      {formatCode(status)}
    </Box>
  ) : null;

// A card of work done together: its name and status on top, its facts and
// the cluster's groups on it beneath
const WorkCard: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    component="li"
    sx={{
      display: "grid",
      gap: 0.5,
      px: 1.25,
      py: 1,
      border: 1,
      borderColor: "divider",
      borderRadius: 1.5,
      backgroundColor: (theme: Theme) => alpha(theme.palette.text.primary, 0.03),
    }}
  >
    {children}
  </Box>
);

// The top line of a card: the name, which opens the entity, and its status
const CardTitle: React.FC<{
  entity: EntityRef;
  status: string | null;
  onOpenEntity: EntityClickHandler;
}> = ({ entity, status, onOpenEntity }) => (
  <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
    <Box sx={{ flex: 1, minWidth: 0, fontWeight: 500 }}>
      <EntityName entity={entity} onOpenEntity={onOpenEntity} sx={{ fontWeight: 500 }} />
    </Box>
    <StatusTag status={status} />
  </Box>
);

// The cluster's groups on a piece of work, each in a chip with its home
// country's flag where one is on record, with its part when one is given;
// a group drawn selects it
const GroupChips: React.FC<{
  groups: PartnershipWorkGroup[];
  drawn: Set<string>;
  onSelect: (selection: PartnershipsSelection) => void;
}> = ({ groups, drawn, onSelect }) => (
  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
    {groups.map(({ group, country, role }) => (
      <OrganizationChip
        key={group.id}
        name={group.name ?? group.id}
        country={country}
        role={role}
        onClick={drawn.has(group.id) ? () => onSelect({ kind: "group", id: group.id }) : undefined}
      />
    ))}
  </Box>
);

// A project's start and end: "2025-03 – 2028", "since 2025", "until 2028"
const spanText = (start: string | null, end: string | null): string =>
  start && end ? `${start} – ${end}` : start ? `since ${start}` : end ? `until ${end}` : "";

// What a card says of a project beneath its name: its types, its span, its
// volume and its countries
const projectFacts = (work: PartnershipProjectWork): string =>
  [
    work.types
      .map((type) => projectTypeMapping.getById(type)?.displayName ?? formatCode(type))
      .join(", "),
    spanText(work.start, work.end),
    work.volume ?? "",
    work.countries.join(", "),
  ]
    .filter(Boolean)
    .join(" · ");

// What a cluster makes together: its products under the kinds they roll up
// to, each a card with its status and the cluster's groups that make it
const ProductsTogether: React.FC<{
  cluster: PartnershipCluster;
  drawn: Set<string>;
  onSelect: (selection: PartnershipsSelection) => void;
  onOpenEntity: EntityClickHandler;
}> = ({ cluster, drawn, onSelect, onOpenEntity }) => {
  const listed = cluster.kinds.reduce((sum, kind) => sum + kind.products.length, 0);
  return (
    <Section label="Products made together" more={cluster.products - listed}>
      {cluster.kinds.map((kind) => (
        <Box
          component="li"
          key={kind.kind?.id ?? "other"}
          aria-label={kind.kind?.name ?? "Other products"}
          sx={{ display: "grid", gap: 0.5 }}
        >
          <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
            <Box sx={{ flex: 1, minWidth: 0, color: "text.secondary", fontWeight: 500 }}>
              {kind.kind ? (
                <EntityName
                  entity={kind.kind}
                  onOpenEntity={onOpenEntity}
                  sx={{ color: "text.secondary", fontWeight: 500 }}
                />
              ) : (
                "Other products"
              )}
            </Box>
            <Typography component="span" color="text.disabled" sx={CAPTION_SX}>
              {counted(kind.count, "product")}
            </Typography>
          </Box>
          <Box component="ul" sx={{ m: 0, p: 0, listStyle: "none", display: "grid", gap: 0.5 }}>
            {kind.products.map((work) => (
              <WorkCard key={work.product.id}>
                <CardTitle entity={work.product} status={work.status} onOpenEntity={onOpenEntity} />
                <GroupChips groups={work.groups} drawn={drawn} onSelect={onSelect} />
              </WorkCard>
            ))}
          </Box>
        </Box>
      ))}
    </Section>
  );
};

// The projects a cluster works on together, each a card with its facts and
// the part each of the cluster's groups has in it
const ProjectsTogether: React.FC<{
  cluster: PartnershipCluster;
  drawn: Set<string>;
  onSelect: (selection: PartnershipsSelection) => void;
  onOpenEntity: EntityClickHandler;
}> = ({ cluster, drawn, onSelect, onOpenEntity }) => (
  <Section label="Projects together" more={cluster.projects - cluster.project_work.length}>
    {cluster.project_work.map((work) => {
      const facts = projectFacts(work);
      return (
        <WorkCard key={work.project.id}>
          <CardTitle entity={work.project} status={work.status} onOpenEntity={onOpenEntity} />
          {facts && (
            <Typography component="div" color="text.secondary" sx={CAPTION_SX}>
              {facts}
            </Typography>
          )}
          <GroupChips groups={work.members} drawn={drawn} onSelect={onSelect} />
        </WorkCard>
      );
    })}
  </Section>
);

interface ClusterPanelProps {
  data: Partnerships;
  cluster: PartnershipCluster;
  // Whether the cluster is the one drawn with all its groups
  expanded: boolean;
  // Asks for all the groups of the cluster, or lets go with null
  onExpand: (expand: number | null) => void;
  onSelect: (selection: PartnershipsSelection) => void;
  onOpenEntity: EntityClickHandler;
}

// The panel of a selected cluster: its name in its colour, its size and
// the strength of its ties inside it and out of it, what it works on
// together, its strongest ties inside, its ties to other clusters, and its
// groups; the way to all of them when only the strongest are drawn
export const ClusterPanel: React.FC<ClusterPanelProps> = ({
  data,
  cluster,
  expanded,
  onExpand,
  onSelect,
  onOpenEntity,
}) => {
  const { chart } = useTheme().palette;
  const byId = new Map(data.nodes.map((node) => [node.id, node]));
  const inCluster = (id: string) => byId.get(id)?.cluster === cluster.id;
  const inside = data.pairs.filter((pair) => inCluster(pair.a) && inCluster(pair.b));
  const across = data.pairs.filter((pair) => inCluster(pair.a) !== inCluster(pair.b));
  const members = data.nodes.filter((node) => node.cluster === cluster.id);
  // The groups of other clusters that work in this one too
  const shared = data.nodes.filter((node) =>
    (node.also ?? []).some((membership) => membership.cluster === cluster.id),
  );
  const colour = clusterColour(cluster.id, chart);
  // The groups drawn, which a chip of theirs selects
  const drawn = new Set(data.nodes.map((node) => node.id));
  return (
    <PanelShell label="Selected cluster">
      <PanelHead head="Cluster" onClose={() => onSelect(null)}>
        <Box component="span" sx={{ display: "inline-flex", alignItems: "baseline", gap: 0.75 }}>
          <Dot colour={colour} />
          <span>{cluster.name}</span>
        </Box>
      </PanelHead>
      <Typography variant="caption" component="div" color="text.secondary" sx={monospaceStyle}>
        {counted(cluster.size, "group")}
        {cluster.shown < cluster.size ? ` · ${cluster.shown} drawn` : ""}
      </Typography>
      <Typography variant="caption" component="div" color="text.secondary" sx={monospaceStyle}>
        strength {strengthText(cluster.internal)} inside, {strengthText(cluster.external)} to
        other clusters
      </Typography>
      {(cluster.size > cluster.shown || expanded) && (
        <Box>
          <Button
            size="small"
            onClick={() => onExpand(expanded ? null : cluster.id)}
            sx={ACTION_SX}
          >
            {expanded ? "Show the strongest only" : `Show all ${cluster.size} groups`}
          </Button>
        </Box>
      )}
      {cluster.kinds.length > 0 && (
        <ProductsTogether
          cluster={cluster}
          drawn={drawn}
          onSelect={onSelect}
          onOpenEntity={onOpenEntity}
        />
      )}
      {cluster.project_work.length > 0 && (
        <ProjectsTogether
          cluster={cluster}
          drawn={drawn}
          onSelect={onSelect}
          onOpenEntity={onOpenEntity}
        />
      )}
      {inside.length > 0 && (
        <Section label="Strongest ties inside" more={inside.length - LISTED}>
          {inside.slice(0, LISTED).map((pair) => (
            <PairRow key={`${pair.a}|${pair.b}`} pair={pair} byId={byId} onSelect={onSelect} />
          ))}
        </Section>
      )}
      {across.length > 0 && (
        <Section label="Ties to other clusters" more={across.length - LISTED}>
          {across.slice(0, LISTED).map((pair) => (
            <PairRow key={`${pair.a}|${pair.b}`} pair={pair} byId={byId} onSelect={onSelect} />
          ))}
        </Section>
      )}
      <Section label="Groups" more={members.length - LISTED * 2}>
        {members.slice(0, LISTED * 2).map((node) => (
          <Box component="li" key={node.id} sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
            <Button
              size="small"
              onClick={() => onSelect({ kind: "group", id: node.id })}
              sx={{ ...ACTION_SX, flex: 1, minWidth: 0 }}
            >
              {groupName(node)}
            </Button>
            <Typography component="span" color="text.disabled" sx={CAPTION_SX}>
              {node.country ?? ""}
            </Typography>
          </Box>
        ))}
      </Section>
      {cluster.shared > 0 && shared.length > 0 && (
        <Section label="Shared with other clusters" more={shared.length - LISTED}>
          {shared.slice(0, LISTED).map((node) => (
            <Box component="li" key={node.id} sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
              <Button
                size="small"
                onClick={() => onSelect({ kind: "group", id: node.id })}
                sx={{ ...ACTION_SX, flex: 1, minWidth: 0 }}
              >
                {groupName(node)}
              </Button>
              <Typography component="span" color="text.disabled" sx={CAPTION_SX}>
                {shareText(
                  (node.also ?? []).find((membership) => membership.cluster === cluster.id)
                    ?.share ?? 0,
                )}
              </Typography>
            </Box>
          ))}
        </Section>
      )}
      <StrengthNote />
    </PanelShell>
  );
};

interface GroupPanelProps {
  data: Partnerships;
  node: PartnershipNode;
  onSelect: (selection: PartnershipsSelection) => void;
  onShowPartners: (id: string) => void;
  onOpenEntity: EntityClickHandler;
}

// A cluster as a chip in its colour, with the share of a group's work in
// it where that is not the group's own cluster; it selects the cluster
const ClusterChip: React.FC<{
  cluster: PartnershipCluster;
  share?: number;
  onSelect: (selection: PartnershipsSelection) => void;
}> = ({ cluster, share, onSelect }) => {
  const { chart } = useTheme().palette;
  return (
    <Chip
      size="small"
      variant="outlined"
      icon={
        // The chip gives its icon's margins by a class, which the dot does
        // not take itself
        <Box component="span" sx={{ display: "inline-flex" }}>
          <Dot colour={clusterColour(cluster.id, chart)} />
        </Box>
      }
      label={
        <>
          {cluster.name}
          {share !== undefined && (
            <Box component="span" sx={{ ...monospaceStyle, color: "text.secondary" }}>
              {" "}
              · {shareText(share)}
            </Box>
          )}
        </>
      }
      onClick={() => onSelect({ kind: "cluster", id: cluster.id })}
      sx={{ height: 22, fontSize: "11.5px", "& .MuiChip-icon": { ml: 1, mr: -0.25 } }}
    />
  );
};

// The work of a group's clusters it has a part in: the products it makes
// and the projects it works on with groups of them, each once
const workOf = (clusters: PartnershipCluster[], id: string) => {
  const products = new Map<string, PartnershipProductWork>();
  const projects = new Map<string, PartnershipProjectWork>();
  for (const cluster of clusters) {
    for (const kind of cluster.kinds) {
      for (const work of kind.products) {
        if (work.groups.some((entry) => entry.group.id === id)) {
          products.set(work.product.id, work);
        }
      }
    }
    for (const work of cluster.project_work) {
      if (work.members.some((entry) => entry.group.id === id)) {
        projects.set(work.project.id, work);
      }
    }
  }
  return { products: Array.from(products.values()), projects: Array.from(projects.values()) };
};

// The panel of a selected group: its flag and its name, which opens the
// organization; where it is from; its clusters; its counts; the products
// and projects of its clusters it has a part in, with whom; its strongest
// ties with the projects shared; and the way to its partners
export const GroupPanel: React.FC<GroupPanelProps> = ({
  data,
  node,
  onSelect,
  onShowPartners,
  onOpenEntity,
}) => {
  const byId = new Map(data.nodes.map((entry) => [entry.id, entry]));
  const ties = data.pairs.filter((pair) => pair.a === node.id || pair.b === node.id);
  const cluster = data.clusters.find((entry) => entry.id === node.cluster) ?? null;
  // The other clusters it belongs to, among those returned
  const also = (node.also ?? []).flatMap((membership) => {
    const other = data.clusters.find((entry) => entry.id === membership.cluster);
    return other ? [{ membership, other }] : [];
  });
  const work = workOf([...(cluster ? [cluster] : []), ...also.map(({ other }) => other)], node.id);
  // The groups drawn, which a chip of theirs selects
  const drawn = new Set(data.nodes.map((entry) => entry.id));
  const others = (groups: PartnershipWorkGroup[]) =>
    groups.filter((entry) => entry.group.id !== node.id);
  const country = node.country ? (countryName(node.country) ?? node.country) : null;
  return (
    <PanelShell label="Selected group">
      <PanelHead head="Group" onClose={() => onSelect(null)}>
        <Box component="span" sx={{ display: "inline-flex", alignItems: "baseline", gap: 0.75 }}>
          {node.country && (
            <CountryFlag code={node.country} size={13} round sx={{ alignSelf: "center" }} />
          )}
          <EntityName entity={node.entity} onOpenEntity={onOpenEntity} sx={TITLE_NAME_SX} />
        </Box>
      </PanelHead>
      <Typography variant="caption" component="div" color="text.secondary" sx={monospaceStyle}>
        organization{country ? ` · ${country}` : ""}
        {node.members > 1 ? ` · ${counted(node.members, "organization")} in the group` : ""}
      </Typography>
      <Typography variant="caption" component="div" color="text.secondary" sx={monospaceStyle}>
        {counted(node.partners, "partner")} · {counted(node.projects, "team project")} ·
        strength {strengthText(node.strength)}
      </Typography>
      <Section label={also.length > 0 ? "Clusters" : "Cluster"}>
        <Box component="li" sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.5 }}>
          {cluster ? (
            <ClusterChip cluster={cluster} onSelect={onSelect} />
          ) : (
            <Typography variant="caption" color="text.secondary">
              In no cluster
            </Typography>
          )}
          {also.length > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
              Also in
            </Typography>
          )}
          {also.map(({ membership, other }) => (
            <ClusterChip key={other.id} cluster={other} share={membership.share} onSelect={onSelect} />
          ))}
        </Box>
      </Section>
      {work.products.length > 0 && (
        <Section label="Products made with partners" more={work.products.length - LISTED}>
          {work.products.slice(0, LISTED).map((entry) => (
            <WorkCard key={entry.product.id}>
              <CardTitle entity={entry.product} status={entry.status} onOpenEntity={onOpenEntity} />
              <GroupChips groups={others(entry.groups)} drawn={drawn} onSelect={onSelect} />
            </WorkCard>
          ))}
        </Section>
      )}
      {work.projects.length > 0 && (
        <Section label="Projects with partners" more={work.projects.length - LISTED}>
          {work.projects.slice(0, LISTED).map((entry) => {
            const role = entry.members.find((member) => member.group.id === node.id)?.role;
            const facts = [role ? `as ${role}` : "", projectFacts(entry)]
              .filter(Boolean)
              .join(" · ");
            return (
              <WorkCard key={entry.project.id}>
                <CardTitle entity={entry.project} status={entry.status} onOpenEntity={onOpenEntity} />
                {facts && (
                  <Typography component="div" color="text.secondary" sx={CAPTION_SX}>
                    {facts}
                  </Typography>
                )}
                <GroupChips groups={others(entry.members)} drawn={drawn} onSelect={onSelect} />
              </WorkCard>
            );
          })}
        </Section>
      )}
      {ties.length > 0 && (
        <Section label="Strongest ties" more={ties.length - LISTED}>
          {ties.slice(0, LISTED).map((pair) => {
            const partner = byId.get(pair.a === node.id ? pair.b : pair.a);
            return (
              <WorkCard key={`${pair.a}|${pair.b}`}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  {partner?.country && <CountryFlag code={partner.country} size={12} round />}
                  <Button
                    size="small"
                    onClick={() => onSelect({ kind: "pair", a: pair.a, b: pair.b })}
                    sx={{ ...ACTION_SX, flex: 1, minWidth: 0, fontWeight: 500 }}
                  >
                    {partner ? groupName(partner) : pair.a === node.id ? pair.b : pair.a}
                  </Button>
                  <Typography component="span" color="text.secondary" sx={CAPTION_SX}>
                    {strengthText(pair.strength)}
                  </Typography>
                </Box>
                <Typography component="div" color="text.disabled" sx={CAPTION_SX}>
                  {tieMakeup(pair)}
                </Typography>
                {pair.shared.length > 0 && (
                  <SharedProjects pair={pair} onOpenEntity={onOpenEntity} />
                )}
              </WorkCard>
            );
          })}
        </Section>
      )}
      <Box sx={{ display: "flex", gap: 1.25, flexWrap: "wrap", mt: 0.75 }}>
        <Button size="small" onClick={() => onShowPartners(node.id)} sx={ACTION_SX}>
          Show partners
        </Button>
      </Box>
    </PanelShell>
  );
};

interface PairPanelProps {
  data: Partnerships;
  pair: PartnershipPair;
  onSelect: (selection: PartnershipsSelection) => void;
  onShowPartners: (id: string) => void;
  onOpenEntity: EntityClickHandler;
}

// The panel of a selected pair: the two names, each opening its
// organization; the strength of the tie and what it is made of; whether a
// partnership is stated; the projects shared; the way to each one's partners
export const PairPanel: React.FC<PairPanelProps> = ({
  data,
  pair,
  onSelect,
  onShowPartners,
  onOpenEntity,
}) => {
  const a = data.nodes.find((node) => node.id === pair.a);
  const b = data.nodes.find((node) => node.id === pair.b);
  const nameOf = (node: PartnershipNode | undefined, id: string) =>
    node ? (
      <EntityName entity={node.entity} onOpenEntity={onOpenEntity} sx={TITLE_NAME_SX} />
    ) : (
      id
    );
  return (
    <PanelShell label="Selected pair">
      <PanelHead head="Pair" onClose={() => onSelect(null)}>
        {nameOf(a, pair.a)}
        <Box component="span" sx={{ color: "text.secondary", mx: 0.75 }}>
          ×
        </Box>
        {nameOf(b, pair.b)}
      </PanelHead>
      <Typography variant="caption" component="div" color="text.secondary" sx={monospaceStyle}>
        strength {strengthText(pair.strength)} · {counted(pair.projects, "shared project")} ·{" "}
        {counted(pair.products, "product")} made together
      </Typography>
      <Box>
        {pair.stated ? (
          <Chip
            label="partnership stated"
            size="small"
            sx={(theme: Theme) => ({
              height: 20,
              fontSize: "11.5px",
              borderColor: alpha(theme.palette.accent.main, 0.45),
              // The text of an accent chip, as the value chain's cards write it
              color: theme.palette.chart.accentText,
              backgroundColor: alpha(theme.palette.accent.main, 0.1),
            })}
          />
        ) : (
          <Typography variant="caption" color="text.secondary">
            No partnership stated
          </Typography>
        )}
      </Box>
      {pair.projects > 0 && (
        <Section label="Shared projects" more={pair.projects - pair.shared.length}>
          {pair.shared.map((project) => (
            <li key={project.id}>
              <EntityName entity={project} onOpenEntity={onOpenEntity} />
            </li>
          ))}
        </Section>
      )}
      {pair.products > 0 && (
        <Section label="Products made together">
          <Box component="li" sx={{ color: "text.secondary" }}>
            {counted(pair.products, "product")} of the scope both make, develop or integrate
          </Box>
        </Section>
      )}
      <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mt: 0.75 }}>
        {[a, b].map(
          (node) =>
            node && (
              <Button
                key={node.id}
                size="small"
                onClick={() => onShowPartners(node.id)}
                sx={ACTION_SX}
              >
                Partners of {groupName(node)}
              </Button>
            ),
        )}
      </Box>
    </PanelShell>
  );
};

interface PartnersPanelProps {
  data: Partnerships;
  focus: PartnershipNode;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  onShowPartners: (id: string) => void;
  onOpenEntity: EntityClickHandler;
}

// The panel of the partners mode, docked beside the rings: the
// organization in focus, which opens it, and its partners ranked by the
// strength of their tie, each with a bar. A partner selected opens out to
// what its tie is made of and the way to its own partners.
export const PartnersPanel: React.FC<PartnersPanelProps> = ({
  data,
  focus,
  selectedId,
  onSelect,
  onShowPartners,
  onOpenEntity,
}) => {
  const { chart } = useTheme().palette;
  const ranked = rankedPartners(data);
  const strongest = Math.max(0, ...ranked.map((entry) => entry.pair?.strength ?? 0));
  return (
    <PanelShell label="Partners" docked>
      <PanelHead head="Partners of">
        <EntityName entity={focus.entity} onOpenEntity={onOpenEntity} sx={TITLE_NAME_SX} />
      </PanelHead>
      <Typography variant="caption" component="div" color="text.secondary" sx={monospaceStyle}>
        organization{focus.country ? ` · ${focus.country}` : ""} ·{" "}
        {counted(focus.projects, "team project")}
      </Typography>
      <Typography variant="caption" component="div" color="text.secondary" sx={monospaceStyle}>
        {counted(focus.partners, "partner")}
        {ranked.length < focus.partners ? ` · the ${ranked.length} strongest drawn` : ""}
      </Typography>
      <Box
        component="ol"
        aria-label="Partners by strength"
        sx={{ m: 0, mt: 0.75, p: 0, listStyle: "none", display: "grid", gap: 1 }}
      >
        {ranked.map(({ node, pair }) => {
          const selected = node.id === selectedId;
          const strength = pair?.strength ?? 0;
          return (
            <Box
              component="li"
              key={node.id}
              data-selected={selected ? "true" : "false"}
              sx={{
                mx: "-6px",
                px: "6px",
                py: selected ? 0.5 : 0,
                borderRadius: "6px",
                backgroundColor: selected ? alpha(chart.ink, 0.04) : undefined,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
                <Dot colour={clusterColour(node.cluster, chart)} />
                <Button
                  size="small"
                  aria-pressed={selected}
                  onClick={() => onSelect(selected ? null : node.id)}
                  sx={{ ...ACTION_SX, flex: 1, minWidth: 0, color: "text.primary" }}
                >
                  {groupName(node)}
                </Button>
                <Typography component="span" color="text.secondary" sx={CAPTION_SX}>
                  {strengthText(strength)}
                </Typography>
              </Box>
              <Box
                aria-hidden
                sx={{ mt: "3px", height: 4, borderRadius: "2px", backgroundColor: alpha(chart.ink, 0.07) }}
              >
                <Box
                  data-testid="pt-bar"
                  sx={{
                    height: 4,
                    borderRadius: "2px",
                    width: `${strongest > 0 ? Math.max(2, (strength / strongest) * 100) : 0}%`,
                    backgroundColor: "accent.main",
                    opacity: selected || selectedId === null ? 1 : 0.55,
                  }}
                />
              </Box>
              {selected && pair && (
                <Box sx={{ mt: 0.5, display: "grid", gap: 0.25 }}>
                  <Typography component="div" color="text.disabled" sx={CAPTION_SX}>
                    {tieMakeup(pair)}
                  </Typography>
                  {pair.shared.length > 0 && (
                    <SharedProjects pair={pair} onOpenEntity={onOpenEntity} />
                  )}
                  <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                    <Button
                      size="small"
                      onClick={() => onOpenEntity(node.entity.id, node.entity.type)}
                      sx={ACTION_SX}
                    >
                      Open
                    </Button>
                    <Button size="small" onClick={() => onShowPartners(node.id)} sx={ACTION_SX}>
                      Show partners
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
      <StrengthNote />
    </PanelShell>
  );
};
